import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';

import { purpurFocus } from '@telialt/common-theme/src/mixins';
import {
    getPurpurBorder,
    getPurpurColor,
    getPurpurSpacing,
    getPurpurTypography,
} from '@telialt/common-theme/src/purpurSelectors';
import { NavigationItem } from '@telialt/models/src/Navigation';

import { MenuItemBadge } from '../MenuItemBadge/MenuItemBadge';
import { displayOption } from '../Navigation.styles';

export const MobileMenuWrapper = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
`;

export const MobileMenuContainer = styled.div`
    flex: 1;
    position: relative;
    overflow: hidden;
`;

export const MobileMenuScrollContainer = styled.div`
    height: 100%;
    overflow: auto;
`;

type OverscrollShadowProps = {
    $isTop: boolean;
    $isVisible: boolean;
};

export const OverscrollShadow = styled.div<OverscrollShadowProps>`
    position: absolute;
    left: 0;
    right: 0;
    height: 2.5rem;
    pointer-events: none;
    display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};

    ${({ $isTop }) =>
        $isTop
            ? css`
                  top: 0;
                  background: linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
              `
            : css`
                  bottom: 0;
                  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
              `}
`;

export const SelectedItemTitle = styled.span`
    flex-shrink: 0;
    text-align: center;
    flex: 1;
    font-weight: ${getPurpurTypography('weightBold')};
    color: ${getPurpurColor('textInteractivePrimaryActive')};
    padding: 0 ${getPurpurSpacing('200')};
    margin-right: ${getPurpurSpacing('300')};
`;

type ItemProps = {
    $displayOption?: NavigationItem['displayOption'];
};

export const MobileMenuItemContainer = styled.div<ItemProps>`
    display: flex;
    align-items: center;
    border-bottom: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('borderWeak')};

    ${({ $displayOption }) => displayOption($displayOption)};
`;

type LinkProps = {
    $colorOption?: NavigationItem['colorOption'];
    $isActive?: boolean;
};

const BaseMenuItem = styled.div<LinkProps>`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 3rem;
    padding: ${getPurpurSpacing('100')} ${getPurpurSpacing('200')};
    color: ${getPurpurColor('textInteractivePrimaryActive')};
    line-height: ${getPurpurTypography('lineHeightDefault')};
    font-size: ${getPurpurTypography('scale100')};
    font-weight: ${getPurpurTypography('weightBold')};
    text-decoration: none;
    color: ${({ $colorOption }) =>
        match($colorOption)
            .with('red', () => getPurpurColor('red500'))
            .otherwise(() => getPurpurColor('textInteractivePrimaryActive'))};

    ${({ $isActive }) =>
        $isActive &&
        css`
            color: ${getPurpurColor('textInteractiveSelected')};
        `};
`;

export const StyledMenuLink = styled(BaseMenuItem).attrs({ as: 'a' })`
    ${purpurFocus('-0.125rem')};

    &:hover {
        color: ${getPurpurColor('textInteractivePrimaryHover')};
    }
    &:active {
        color: ${getPurpurColor('textInteractivePrimaryActive')};
    }
    &:focus:focus-visible {
        color: ${getPurpurColor('textInteractivePrimary')};
    }
`;

export const StyledMenuButton = styled(StyledMenuLink).attrs({ as: 'button' })``;

export const MobileMenuItemContent = styled.span`
    display: flex;
    align-items: center;
    gap: ${getPurpurSpacing('50')};
`;

export const SelectedItemBackButton = styled(StyledMenuLink).attrs({ as: 'button' })`
    display: flex;
    align-items: center;
    width: 100%;
    height: 3.5rem;
    padding-left: ${getPurpurSpacing('200')};
    padding-right: calc(${getPurpurSpacing('200')} + 1.5rem);
    border-bottom: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('borderWeak')};
    overflow: hidden;
`;

export const MobileMenuItemBadge = styled(MenuItemBadge)`
    margin-left: ${getPurpurSpacing('100')};
`;
